import { ActivityAction, ActivityItem, ActivityType } from 'app/data/types/activity';
import { DEEPLINK_CONFIG } from 'app/shared/consts/activity';

/** Base urls for the target miniapps, keyed by the env var name in `DEEPLINK_CONFIG`. */
export type DeeplinkEnvUrls = Record<string, string | undefined>;

const joinUrl = (base: string, route: string): string =>
  `${base.replace(/\/+$/, '')}/${route.replace(/^\/+/, '')}`;

/**
 * The map + resource-list screen in miniapp-resource-booking.
 *
 * Prefixed, unlike the record routes (`receipt/:uuid`, `resources/:uuid`) which sit at the root — the
 * miniapp serves this screen under its own segment.
 */
export const RESOURCE_BOOKING_RESOURCES_ROUTE = 'resource-booking/resources';

/** The origin of a url, or `undefined` if it isn't absolute. Button urls are authored by hand. */
const originOf = (url: string | undefined | null): string | undefined => {
  if (!url) return undefined;

  try {
    return new URL(url).origin;
  } catch {
    return undefined;
  }
};

/**
 * Whether `url` points at HqO's OWN miniapp for `type` — the same app our create deeplink opens.
 *
 * This is the question that decides whether our create flow can stand in for a building's utility
 * button (VIS-2189). Asked of the url rather than of the integration behind it, because the url is
 * the thing both destinations are actually built from: when a button opens our miniapp, our create
 * link is the same app one screen deeper, and when it opens anywhere else no adapter name makes our
 * flow the right destination. The two came apart exactly where it mattered — Cloudgate is an
 * integration our visitor flow creates into AND a button that links out to Cloudgate's own site.
 *
 * Compared by ORIGIN, not by whole url: the button points at the module's home and our deeplink at a
 * route inside it, so they agree on everything up to the path and nowhere after it.
 *
 * `false` when either side is unknown — an un-parseable button url, or an env var this build does not
 * set. Both mean "we cannot show that these are the same place", and the caller treats not-our-module
 * as a reason to follow the button rather than to speak for it.
 */
export const opensOurModule = (
  url: string | undefined | null,
  type: ActivityType,
  envUrls: DeeplinkEnvUrls,
): boolean => {
  const buttonOrigin = originOf(url);
  if (!buttonOrigin) return false;

  const config = DEEPLINK_CONFIG[type];
  if (!config) return false;

  return buttonOrigin === originOf(envUrls[config.envKey]);
};

/**
 * Is this url one of OUR miniapps — any of them, not just the one a given activity type belongs to?
 *
 * `opensOurModule` answers a routing question and so is scoped to one type. This answers a trust
 * question: whether the destination is somewhere we may hand HqO's launch parameters, the reader's
 * auth token among them. A button can legitimately point at a sibling miniapp, which is ours to
 * bootstrap; anything else is a third party and gets the url alone.
 *
 * Unknown on either side is `false`. Withholding the parameters from somewhere that turns out to be
 * ours costs a miniapp its bootstrap; sending them somewhere that turns out not to be costs a
 * credential.
 */
export const isOurOrigin = (url: string, envUrls: DeeplinkEnvUrls): boolean => {
  const target = originOf(url);
  if (!target) return false;

  return Object.values(envUrls).some((base) => originOf(base) === target);
};

/**
 * Build the webview url for a card action, client-side, from the static per-type template + the
 * action's `routingDetails` + the item's `buildingUuid` (data-contract §3). Returns `null` — so the
 * caller omits the action rather than dead-tapping — when the type is unknown, the base url env is
 * unset, or a required routing id is missing.
 *
 * `inPane` says the host frames the record in a COLUMN beside the feed rather than giving it a page.
 * Only the caller knows that, and only resource bookings currently answer differently.
 */
export const buildActivityDeeplink = (
  item: ActivityItem,
  action: ActivityAction,
  buildingUuid: string | undefined,
  envUrls: DeeplinkEnvUrls,
  { inPane = false }: { inPane?: boolean } = {},
): string | null => {
  const config = DEEPLINK_CONFIG[item.type];
  if (!config) return null;

  // A record can live in a different miniapp than the module landing (events).
  const base = envUrls[config.recordEnvKey ?? config.envKey];
  if (!base) return null;

  /*
   * A column and a page are not the same destination for every type — a resource booking opens its
   * own bookings page where there is room for one, and the single booking where there is not. Types
   * with no `buildInPane` answer the same thing either way, which is every type but that one.
   */
  const route = inPane && config.buildInPane
    ? config.buildInPane(action.routingDetails)
    : config.build(action.routingDetails, buildingUuid ?? item.context.buildingUuid, action.type, item);
  if (route == null) return null;

  return joinUrl(base, route);
};

/**
 * Resolve a module-level url. `landing` opens the module home (the fallback when a record can't be
 * routed); `create` opens its creation flow, which is what the empty-state CTAs ("Submit a request",
 * "Register a visitor") promise. Returns `null` when the type or base url is unknown.
 *
 * `appInstanceUuid` is the config behind the building's button for the vertical. Only the visitor
 * create flow reads it (VIS-2189): the visitor MFE sends it on as `HqO-App-Instance-UUID`, and without
 * it visitor-registration-service resolves the integration by building, which on a building with more
 * than one visitor config need not be the one its button uses.
 */
export const buildBaseDeeplink = (
  item: Pick<ActivityItem, 'type' | 'context'>,
  envUrls: DeeplinkEnvUrls,
  buildingUuid?: string,
  intent: 'landing' | 'create' = 'landing',
  { appInstanceUuid }: { appInstanceUuid?: string } = {},
): string | null => {
  const config = DEEPLINK_CONFIG[item.type];
  if (!config) return null;

  const base = envUrls[config.envKey];
  if (!base) return null;

  if (intent === 'create' && item.type === 'visitor_registration') {
    const bUuid = buildingUuid ?? item.context.buildingUuid;
    if (!bUuid) return null;
    const host = base.replace(/\/+$/, '');
    const instance = appInstanceUuid ? `&appInstanceConfigUuid=${encodeURIComponent(appInstanceUuid)}` : '';
    return `${host}/visitor-registration/building/${bUuid}/visits/create?buildingUuid=${bUuid}${instance}`;
  }

  // work-orders-end-user registers its routes at the root as well as under `configs/:configUuid`.
  if (intent === 'create' && item.type === 'work_order') return joinUrl(base, 'new');

  // Both intents, unlike the two above. The resource-booking root renders nothing a tenant can act on,
  // so landing there — which is where an unroutable booking used to fall back — reads as a dead end.
  // `resources` is the map + list screen, which serves as the module home AND as the booking flow's
  // entry point, so `create` wants the same place.
  if (item.type === 'resource_booking') return joinUrl(base, RESOURCE_BOOKING_RESOURCES_ROUTE);

  return base;
};

/** The item's primary (first) action, if any. */
export const getPrimaryAction = (item: ActivityItem): ActivityAction | undefined => item.actions?.[0];

/**
 * The action that opens the RECORD, wherever it sits in the list.
 *
 * Not the same question as {@link getPrimaryAction}, which answers "what does this tile do by
 * default" and takes the first. An item can offer something else first — a booking whose CTA is
 * "Book again" — and the two then disagree about which action is in play.
 *
 * Shared because two places must not drift on it: the in-app transaction screen picks its
 * destination from this action, and the selection written for the host's pane has to describe
 * THAT destination. Answering the first question in one and the second in the other sent the
 * reader to one booking and remembered another.
 */
export const getRecordAction = (item: ActivityItem): ActivityAction | undefined =>
  item.actions?.find((candidate) => candidate.type === 'open_record');
